/**
 * Image preview with a fullscreen escape hatch (#510).
 *
 * The fitted view scales an image into whatever space it is given — in Drive
 * 2.0's split pane that is a narrow column, which leaves a wide screenshot
 * unreadable and offers no zoom. Fullscreen is a fixed overlay that closes on a
 * backdrop click or Escape.
 *
 * The overlay itself now lives in the shared ImageLightbox (#714), which the
 * Communicator's split-view preview uses as well.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Maximize2 } from 'lucide-react'
import { ImageLightbox } from '@/components/shared/ImageLightbox'
import type { BinaryRendererProps } from '../types'

interface ImageRendererProps extends BinaryRendererProps {
  fileName?: string
}

export function ImageRenderer({ fileUrl, fileName }: ImageRendererProps) {
  const { t } = useTranslation()
  const [error, setError] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <AlertCircle className="h-8 w-8 text-[var(--text-secondary)]" />
        <span className="text-sm text-[var(--text-secondary)]">
          {t('filePreview.errorLoading')}
        </span>
      </div>
    )
  }

  return (
    <div className="relative flex flex-1 min-h-0 items-center justify-center overflow-auto p-4">
      <button
        type="button"
        onClick={() => setIsFullscreen(true)}
        aria-label={t('filePreview.fullscreen')}
        className="flex max-h-full max-w-full"
        // Inline, not `cursor-zoom-in`: Tailwind v4's preflight sets
        // `button { cursor: default }`, and relying on layer order to win that
        // is exactly the kind of silent breakage this affordance can't afford.
        style={{ cursor: 'zoom-in' }}
      >
        <img
          src={fileUrl}
          alt={fileName ?? ''}
          className="max-h-full max-w-full object-contain"
          onError={() => setError(true)}
        />
      </button>
      {/* Always visible, and it says what it does. A hover-only icon is no
          affordance: it needs the pointer to already be there to reveal that
          there was anything to point at. */}
      <button
        onClick={() => setIsFullscreen(true)}
        className="absolute right-4 top-4 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium shadow-sm transition-opacity"
        style={{
          backgroundColor: 'var(--window-background)',
          color: 'var(--text-color)',
          border: '1px solid var(--window-border-color)',
          cursor: 'pointer',
        }}
      >
        <Maximize2 className="h-3.5 w-3.5" />
        {t('filePreview.fullscreen')}
      </button>
      {isFullscreen && (
        <ImageLightbox
          src={fileUrl}
          alt={fileName}
          onClose={() => setIsFullscreen(false)}
          onError={() => setError(true)}
        />
      )}
    </div>
  )
}
